import React, { useMemo } from 'react';
import { useParams, useHistory, Redirect } from 'react-router-dom';
import { Box, Flex, Text } from 'rebass/styled-components';
import { useSelector } from 'react-redux';
import { Loader } from '@getro/rombo';
import usePageTitle from '../../hooks/usePageTitle';
import MainLayout from '../../components/layout/Main';
import { MainLayoutHeader } from '../../components/atoms/mainLayoutHeader';
import DataTable from '../../components/molecules/agGrid';
import { InfoBox } from '../../components/atoms/InfoBox';
import { useGetSourcingCampaignQuery } from '../../services/sourcing';
import { useCandidatesTableRows, getCandidatesColumns } from './hooks/useCandidatesTableRows';
import * as renderers from './renderers';
import { CampaignEmptyState } from './components/campaignEmptyState';
import { activeNetworkSelector } from '../../redux/selectors';
import { statuses } from './helper';

const SourcingCampaign = () => {
  const { id } = useParams();
  const { data: campaign, isLoading } = useGetSourcingCampaignQuery({ id });
  const activeNetwork = useSelector(activeNetworkSelector);
  const candidates = campaign?.clickedCandidates || [];

  usePageTitle(campaign?.campaignName || 'Campaign Details');

  const candidatesColumns = useMemo(
    () => getCandidatesColumns({ candidateCount: candidates.length }),
    [candidates.length],
  );
  const tableRows = useCandidatesTableRows({ candidates });
  const history = useHistory();

  if (isLoading) {
    return (
      <MainLayout>
        <Box p="24px" display="flex" justifyContent="center" alignItems="center" height="calc(100vh - 40px)">
          <Loader />
        </Box>
      </MainLayout>
    );
  }

  if (!campaign) {
    return <Redirect to={`/networks/${activeNetwork.slug}/sourcing-agent`} />;
  }

  const statusConfig = statuses[campaign.status] || {};

  return (
    <MainLayout>
      <MainLayoutHeader
        breadcrumbs={[
          { label: 'Sourcing Agent', onClick: () => history.push(`/networks/${activeNetwork.slug}/sourcing-agent`) },
        ]}
        title={campaign.campaignName}
      />

      <Flex
        sx={{
          gap: '16px',
          px: '16px',
          pb: '26px',
          mt: '24px',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: '400',
          lineHeight: '140%',
        }}
      >
        <InfoBox
          count={statusConfig.label}
          title={statusConfig.infoTitle}
          infoText={<Text color="text.main">{statusConfig.info}</Text>}
          popoverPosition="bottom-end"
          popoverContentSx={{ width: '320px' }}
          width="1/3"
        />
        <InfoBox
          count={campaign.candidatesEmailedCount || 0}
          title="Candidates emailed so far"
          showCount
          width="1/3"
          sx={{
            minWidth: '1/3',
          }}
          infoText={
            <Flex sx={{ flexDirection: 'column', gap: '16px', color: 'text.main' }}>
              <Text>
                The Agent typically reaches up to 250 qualified candidates per campaign (fewer for niche roles).
              </Text>
              <Text>Emails are sent gradually, so this number will increase as outreach continues.</Text>
            </Flex>
          }
          popoverPosition="bottom-end"
          popoverContentSx={{ width: '320px' }}
        />
        <InfoBox
          count={campaign.candidatesClickedCount || 0}
          title="Candidates clicked 'Apply'"
          showCount
          width="1/3"
          infoText={
            <Flex sx={{ flexDirection: 'column', gap: '16px', color: 'text.main' }}>
              <Text>
                The number of emailed candidates who have clicked the &apos;Apply&apos; link in the outreach emails.
              </Text>
              <Text>Because they apply directly on the companies&apos; ATS, we can&apos;t verify if they applied.</Text>
            </Flex>
          }
          popoverPosition="bottom-end"
          popoverContentSx={{ width: '320px' }}
        />
      </Flex>
      {candidates.length > 0 && (
        <Box
          sx={{
            borderTop: '1px solid',
            borderColor: 'border.subtle',
          }}
        >
          <DataTable
            showActions={false}
            useHorizontalActions={false}
            fixedColumn="first"
            items={tableRows}
            columnDefinition={candidatesColumns}
            hasMore={false}
            isInitialized={!isLoading}
            isLoadingMore={false}
            persistColumnKey="sourcing-campaign-candidates-v4"
            gridOptions={{
              suppressRowClickSelection: false,
              cellSelection: true,
              animateRows: false,
              rowHeight: null, // Override default to enable auto-height
            }}
            components={{
              candidateNameRenderer: renderers.CandidateNameRenderer,
              candidateCompanyRenderer: renderers.CandidateCompanyRenderer,
              campaignStrengthsRenderer: renderers.CampaignStrengthsRenderer,
            }}
            sx={{
              height: 'calc(100vh - 257px)!important',
              '& .ag-row': {
                // minHeight: '70px !important',
              },
              '& .ag-cell': {
                lineHeight: '1.5',
                whiteSpace: 'normal',
              },
              '& .ag-cell-auto-height': {
                // padding: '16px 8px !important',
              },
            }}
            cellStyle={{
              padding: '16px 8px',
            }}
          />
        </Box>
      )}
      {candidates.length === 0 && <CampaignEmptyState status={campaign.status} statuses={statuses} />}
    </MainLayout>
  );
};

export default SourcingCampaign;
